import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BookOpen, CalendarDays, Loader2, MapPin, X } from 'lucide-react';

import { getPublicJourney } from '../../services/api/journeysApi';
import type {
  JourneyMapPhotoItem,
  JourneyPhotoMeta,
  JourneyPublicDetail,
} from '../../types/journey';
import { useAppLanguage } from '../../hooks/useAppLanguage';
import { useTranslation } from '../../useTranslation';
import { formatJourneyDateRange } from '../../utils/journeyDateRange';

(window as unknown as { L: typeof L }).L = L;

function formatDay(iso: string, language: string): string {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString(
      language?.toLowerCase().startsWith('ru') ? 'ru-RU' : 'en-US',
      { weekday: 'short', day: 'numeric', month: 'long' },
    );
  } catch {
    return iso;
  }
}

function thumbIcon(url: string): L.DivIcon {
  const safe = url.replace(/"/g, '&quot;');
  return L.divIcon({
    className: '',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    html: `<div style="width:40px;height:40px;border-radius:8px;overflow:hidden;border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35)"><img src="${safe}" alt="" style="width:100%;height:100%;object-fit:cover"/></div>`,
  });
}

const PublicMap: React.FC<{ photos: JourneyMapPhotoItem[]; onOpen: (p: JourneyMapPhotoItem) => void }> = ({
  photos,
  onOpen,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current || photos.length === 0) return;
    const map = L.map(ref.current, { scrollWheelZoom: true });
    L.tileLayer('/api/v1/map-tiles/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
      crossOrigin: true,
    }).addTo(map);
    const bounds: L.LatLngExpression[] = [];
    for (const p of photos) {
      L.marker([p.lat, p.lng], { icon: thumbIcon(p.url) })
        .on('click', () => onOpen(p))
        .addTo(map);
      bounds.push([p.lat, p.lng]);
    }
    if (bounds.length === 1) map.setView(bounds[0], 12);
    else map.fitBounds(L.latLngBounds(bounds), { padding: [36, 36], maxZoom: 14 });
    setTimeout(() => map.invalidateSize(), 50);
    return () => {
      map.remove();
    };
  }, [photos, onOpen]);
  if (photos.length === 0) return null;
  return <div ref={ref} className="h-64 sm:h-80 w-full rounded-2xl overflow-hidden border border-stone-200 shadow-sm" />;
};

export const SharedJourneyPage: React.FC = () => {
  const { language } = useAppLanguage();
  const { t } = useTranslation(language);
  const token = useMemo(() => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    return parts[0] === 'j' ? parts[1] || '' : '';
  }, []);
  const [detail, setDetail] = useState<JourneyPublicDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<JourneyPhotoMeta | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Not found');
      setLoading(false);
      return;
    }
    getPublicJourney(token)
      .then(setDetail)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-stone-100">
        <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-stone-100 p-6">
        <div className="max-w-md text-center space-y-2">
          <BookOpen className="h-10 w-10 mx-auto text-stone-400" />
          <h1 className="text-lg font-semibold text-stone-800">{t.journeysShareNotFound}</h1>
          <p className="text-sm text-stone-500">{error || t.journeysShareNotFoundHint}</p>
          <a href="/" className="inline-block mt-3 text-sm font-semibold text-sky-700 hover:underline">
            MyTravelDiary
          </a>
        </div>
      </div>
    );
  }

  const sorted = [...(detail.entries || [])].sort((a, b) => a.entryDate.localeCompare(b.entryDate));
  const album = detail.albumPhotos || [];
  const mapPhotos = detail.mapPhotos || [];

  return (
    <div className="min-h-dvh bg-gradient-to-b from-stone-100 to-stone-200 text-stone-900">
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/90 p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightbox.url}
            alt={lightbox.filename}
            className="max-h-[90vh] max-w-[95vw] object-contain rounded-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      <header className="relative overflow-hidden min-h-[12rem] border-b border-stone-200/80">
        {detail.coverUrl ? (
          <>
            <img
              src={detail.coverUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: `${detail.coverFocusX ?? 50}% ${detail.coverFocusY ?? 50}%` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-stone-950/35 to-stone-950/10" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-sky-100 via-amber-50 to-stone-100" />
        )}
        <div className="relative z-10 max-w-3xl mx-auto px-4 py-8 sm:py-10">
          <p className="text-xs font-semibold uppercase tracking-wide text-white/80 mb-2">MyTravelDiary</p>
          <h1 className={`text-3xl font-semibold tracking-tight ${detail.coverUrl ? 'text-white' : 'text-stone-900'}`}>
            {detail.tag}
          </h1>
          {(detail.dateFrom || detail.dateTo) && (
            <p className={`mt-2 inline-flex items-center gap-1.5 text-sm ${detail.coverUrl ? 'text-white/85' : 'text-stone-600'}`}>
              <CalendarDays className="h-4 w-4" />
              {formatJourneyDateRange(detail.dateFrom, detail.dateTo, t.journeysDateUnknown)}
            </p>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6 pb-16">
        {mapPhotos.length > 0 && (
          <section className="space-y-2">
            <h2 className="text-sm font-semibold flex items-center gap-2 text-stone-800">
              <MapPin className="h-4 w-4 text-sky-600" />
              {t.journeysMapViewMap}
            </h2>
            <PublicMap
              photos={mapPhotos}
              onOpen={p =>
                setLightbox({
                  id: p.id,
                  filename: p.tag,
                  mime: 'image/webp',
                  url: p.url,
                  entryDate: p.entryDate,
                  lat: p.lat,
                  lng: p.lng,
                })
              }
            />
          </section>
        )}

        {sorted.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-sky-600" />
              {t.journeysDiary}
            </h2>
            <ol className="space-y-4">
              {sorted.map(entry => (
                <li
                  key={entry.entryDate}
                  className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm space-y-3"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                    {formatDay(entry.entryDate, language)}
                  </p>
                  {entry.content && (
                    <p className="text-sm leading-relaxed whitespace-pre-wrap text-stone-800">{entry.content}</p>
                  )}
                  {(entry.photos || []).length > 0 && (
                    <div className="grid grid-cols-3 gap-1.5">
                      {(entry.photos || []).map(p => (
                        <button
                          key={p.id}
                          type="button"
                          className="aspect-square rounded-lg overflow-hidden"
                          onClick={() => setLightbox(p)}
                        >
                          <img src={p.url} alt="" className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {album.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-stone-800">{t.journeysAlbum}</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
              {album.map(p => (
                <button
                  key={p.id}
                  type="button"
                  className="aspect-square rounded-lg overflow-hidden border border-stone-200"
                  onClick={() => setLightbox(p)}
                >
                  <img src={p.url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default SharedJourneyPage;
